import { memo } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { GenerationToolIcon } from 'src/components/GenerationToolIcon';
import { GenerationToolLabel } from 'src/components/GenerationToolLabel';
import { TruncatedTooltip } from 'src/components/TruncatedTooltip';
import {
  GENERATION_TOOL,
  getToolDescription,
  getToolLabel,
  getToolTitle,
  mapGenerationTool,
  toolHasDescriptionInTitle,
} from 'src/data/generationTool';

import { useStyles } from './useStyles';

const prepareDescription = ({ tool, hideDescription, descriptionOverride }) => {
  if (hideDescription) {
    return '';
  }

  if (descriptionOverride) {
    return descriptionOverride;
  }

  if (toolHasDescriptionInTitle(tool)) {
    return '';
  }

  return getToolDescription(tool);
};

export const GenerationToolItem = memo(
  ({
    className,
    dataTestId,
    classes,
    tool,
    boldTitle,
    toolTitle,
    grayDescription,
    hideDescription,
    boldDescription,
    descriptionOverride,
    hideIcon,
    iconImage,
    hideSeparator,
    replaceIcon,
  }) => {
    const styles = useStyles({ classes });

    const mappedTool = mapGenerationTool(tool);

    const title = toolTitle || getToolTitle(mappedTool);
    const description = prepareDescription({
      tool: mappedTool,
      hideDescription,
      descriptionOverride,
    });
    const tooltip = toolTitle || getToolLabel(mappedTool, { description });

    return (
      <div
        data-testid={dataTestId || `${tool}_GenerationToolItem`}
        className={cx(styles.root, className)}
      >
        {!hideIcon &&
          (replaceIcon ? (
            replaceIcon
          ) : (
            <GenerationToolIcon
              className={styles.icon}
              classes={{ iconSvgString: styles.iconSvgString }}
              iconSvgString={iconImage}
              tool={mappedTool}
            />
          ))}

        <TruncatedTooltip title={tooltip}>
          {(ref) => (
            <GenerationToolLabel
              className={styles.label}
              ref={ref}
              label={title}
              description={description}
              boldLabel={boldTitle}
              grayDescription={grayDescription}
              boldDescription={boldDescription}
              hideSeparator={hideSeparator}
            />
          )}
        </TruncatedTooltip>
      </div>
    );
  }
);

GenerationToolItem.propTypes = {
  className: PropTypes.string,
  dataTestId: PropTypes.string,
  classes: PropTypes.shape(),
  tool: PropTypes.oneOf(Object.values(GENERATION_TOOL)).isRequired,
  boldTitle: PropTypes.bool,
  toolTitle: PropTypes.string,
  grayDescription: PropTypes.bool,
  hideDescription: PropTypes.bool,
  boldDescription: PropTypes.bool,
  descriptionOverride: PropTypes.string,
  hideIcon: PropTypes.bool,
  iconImage: PropTypes.string,
  hideSeparator: PropTypes.bool,
  replaceIcon: PropTypes.element,
};

GenerationToolItem.defaultProps = {
  className: '',
  dataTestId: null,
  classes: null,
  boldTitle: false,
  toolTitle: null,
  grayDescription: false,
  hideDescription: false,
  boldDescription: false,
  descriptionOverride: null,
  hideIcon: false,
  iconImage: null,
  hideSeparator: false,
  replaceIcon: null,
};
